import { CARD_DESCRIPTORS } from "./metrics";

export const DEFAULT_CARD_ORDER: readonly number[] = CARD_DESCRIPTORS.map((d) => d.index);
const VALID = new Set(DEFAULT_CARD_ORDER);

const validIndices = (raw: unknown): number[] =>
  Array.isArray(raw) ? raw.filter((i): i is number => Number.isInteger(i) && VALID.has(i)) : [];

/** Persisted disabled indices: drop junk and duplicates. */
export function sanitizeDisabledCards(raw: unknown): number[] {
  return [...new Set(validIndices(raw))];
}

/** Persisted order: drop junk/duplicates, then append any missing index in default order. */
export function sanitizeCardOrder(raw: unknown): number[] {
  const seen = [...new Set(validIndices(raw))];
  return [...seen, ...DEFAULT_CARD_ORDER.filter((i) => !seen.includes(i))];
}

export function isDefaultCardOrder(order: readonly number[]): boolean {
  return order.length === DEFAULT_CARD_ORDER.length && order.every((v, i) => v === DEFAULT_CARD_ORDER[i]);
}

/** Sort anything carrying `.index` by `order`. Items missing from `order` go last, in input order. */
export function applyCardOrder<T extends { index: number }>(items: readonly T[], order: readonly number[]): T[] {
  const rank = new Map(order.map((idx, pos) => [idx, pos]));
  return [...items].sort((a, b) => (rank.get(a.index) ?? Infinity) - (rank.get(b.index) ?? Infinity));
}

/** Move `index` one slot left/right (-1/+1). Returns the same array reference when it can't move. */
export function moveCardByDelta(order: readonly number[], index: number, delta: -1 | 1): readonly number[] {
  const from = order.indexOf(index);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= order.length) return order;
  const next = [...order];
  next.splice(to, 0, next.splice(from, 1)[0]!);
  return next;
}

/** Selected cards that are not disabled: what card outlines and the chart render. */
export function resolveVisibleSelection(selected: readonly number[], disabled: readonly number[]): Set<number> {
  const off = new Set(disabled);
  return new Set(selected.filter((i) => !off.has(i)));
}
